<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Virtual Tours können bei Google ranken – aber nur, wenn Sie sie
            richtig optimieren. Die meisten Virtual-Tour-Ersteller
            veröffentlichen eine Tour und erwarten, dass sie organisch entdeckt
            wird. Ohne ordentliches SEO existiert Ihre Tour in einem digitalen
            Vakuum. Dieser Leitfaden deckt jede technische und inhaltliche
            SEO-Maßnahme ab, die Sie ergreifen können, um Ihre Virtual Tours bei
            Google im Jahr 2026 höher zu platzieren.
          </p>
        </section>

        <section>
          <h2>1. Erstellen Sie eine dedizierte Landingpage für jede Tour</h2>
          <p>
            Betten Sie Ihre Tour nicht einfach ein und betrachten Sie die Arbeit
            als erledigt. Jede Tour braucht eine dedizierte SEO-optimierte
            Landingpage mit: einem keyword-gezielten H1, mindestens 500 Wörtern
            originellem beschreibendem Inhalt über die Immobilie oder den
            Standort, korrektem Meta-Titel und Meta-Beschreibung sowie dem
            Tour-Einbettung oberhalb der Falzlinie. Diese Seite ist das, was
            Google indexiert – die Tour selbst ist nur interaktiver Inhalt
            darin.
          </p>
        </section>

        <section>
          <h2>2. Schema-Markup für Virtual Tours verwenden</h2>
          <p>
            Fügen Sie <code>TouristAttraction</code>- oder
            <code>LocalBusiness</code>-Schema zu Virtual-Tour-Landingpages
            hinzu. Für Immobilienanzeigen verwenden Sie
            <code>RealEstateListing</code>-Schema. Für Business Virtual Tours
            verwenden Sie <code>LocalBusiness</code>-Schema mit einer
            <code>hasMap</code>-Eigenschaft, die auf Ihren Google Maps Eintrag
            verweist. Diese strukturierten Datentypen helfen Google zu
            verstehen, worum es bei Ihrer Tour geht.
          </p>
        </section>

        <section>
          <h2>3. Für lokale Keywords optimieren</h2>
          <p>
            Virtual Tours sind von Natur aus lokal. Integrieren Sie
            standortspezifische Keywords: „[Stadt] Virtual Tour", „[Stadtteil]
            Immobilientour", „[Straße] Immobilien Virtual Tour". Verwenden Sie
            Googles lokale Pack-Keywords natürlich in Ihrem Landingpage-Inhalt.
            Geo-taggen Sie die Seite mit den
            <code>geo.position</code>- und <code>ICBM</code>-Meta-Tags mit den
            Koordinaten Ihrer Immobilie.
          </p>
        </section>

        <section>
          <h2>4. Backlinks aus lokalen Verzeichnissen aufbauen</h2>
          <p>
            Reichen Sie Ihre Virtual-Tour-Landingpages bei lokalen
            Unternehmensverzeichnissen, Immobilienportalen und Tourismuswebsites
            ein. Jeder Backlink signalisiert Google lokale Relevanz. Wichtige
            Verzeichnisse: Google Business Profil, Bing Places, Yelp,
            TripAdvisor (für Gastgewerbe), Zillow/Rightmove (für Immobilien).
          </p>
        </section>

        <section>
          <h2>5. Seitenladegeschwindigkeit optimieren</h2>
          <p>
            Virtual Tours sind datenintensiv. Verwenden Sie Lazy Loading für den
            Tour-iFrame – laden Sie ihn nicht, bis der Nutzer dorthin scrollt.
            Dies reduziert die anfängliche Seitenladezeit dramatisch und
            verbessert die Core Web Vitals-Scores, die sich direkt auf das
            Google-Ranking auswirken.
          </p>
        </section>

        <section>
          <h2>6. Tour zu Ihrem Google Business Profil hinzufügen</h2>
          <p>
            Virtual Tours im Google Business Profil erscheinen direkt in Suche
            und Maps-Ergebnissen. Dies ist die wichtigste SEO-Maßnahme für
            lokale Business Virtual Tours – sie bringt Ihre Tour vor Menschen,
            die aktiv nach Ihrem Unternehmenstyp in Ihrer Region suchen.
          </p>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/360-fotos-lokales-seo">360°-Fotos für lokales SEO : Ranking verbessern</a></li>
            <li><a href="/blog/de/google-business-virtual-tour">Google Business Virtual Tour : Anleitung</a></li>
            <li><a href="/blog/de/google-street-view-virtual-tour">Google Street View Virtual Tour : Schritt für Schritt</a></li>
            <li><a href="/blog/de/virtual-tour-analysen">Virtual Tour Analysen : Besucher verstehen</a></li>
            <li><a href="/blog/de/360-virtual-tour-website-einbetten">360°-Virtual-Tour in Website einbetten</a></li>
            <li><a href="/blog/de/virtual-tour-lead-generierung">Virtual Tour Lead-Generierung</a></li>
            <li><a href="/blog/de/virtual-tour-google-business-profil">Virtual Tour Google Business Profil</a></li>
            <li><a href="/blog/de/virtual-tour-wordpress-einbetten">Virtual Tour WordPress einbetten</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "virtual-tour-seo-leitfaden",
);

useBlogSeo({
  title: "Virtual Tour SEO [2026]: Rankings verbessern | Travvir",
  description:
    "Virtual Tours für SEO optimieren und bessere Rankings erzielen. Technische SEO, strukturierte Daten und Content-Strategie.",
  slug: "virtual-tour-seo-leitfaden",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
